import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Filter, ArrowLeft, ExternalLink, Sparkles } from "lucide-react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ChallengeRing from "@/components/ChallengeRing";
import { useAuth } from "@/context/AuthContext";

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
// Public leaderboard endpoints — deliberately DO NOT go through the authed axios instance.
const publicApi = axios.create({ baseURL: `${BACKEND_ORIGIN}/api` });

const SORTS = [
  { value: "best_pnl",        label: "Best P&L" },
  { value: "least_drawdown",  label: "Least Drawdown" },
  { value: "fastest_pass",    label: "Fastest Pass" },
  { value: "newest",          label: "Newest" },
];

const money = (v) => `${v >= 0 ? "+" : ""}${Number(v || 0).toFixed(2)}%`;

const EntryCard = ({ e }) => {
  const passed = e.status === "passed";
  const failed = e.status === "failed";
  const statusColor = passed ? "#f59e0b" : failed ? "#ef4444" : "#10b981";
  const statusLabel = passed ? "PASSED" : failed ? "FAILED" : "ACTIVE";
  const pnlPositive = Number(e.current_pnl_pct || 0) >= 0;
  return (
    <div className="vx-card p-4 hover:bg-[#1f2937]/30 transition-colors" data-testid={`leaderboard-card-${e.id}`}>
      <div className="flex items-start gap-3">
        <ChallengeRing
          progressPct={e.current_pnl_pct}
          targetPct={e.profit_target_pct}
          drawdownPct={e.current_drawdown_pct}
          maxDrawdownPct={e.max_total_drawdown_pct}
          daysRemaining={e.days_remaining}
          status={e.status}
          size={80}
          strokeWidth={7}
          label={e.firm_name}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl leading-none" aria-hidden="true">{e.avatar_emoji || "🎯"}</span>
            <div className="font-heading font-black text-lg tracking-tight truncate" title={e.display_name}>
              {e.display_name}
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase text-[#9ca3af] tracking-widest mt-0.5">
            {e.firm_name} · {(e.challenge_type || "").replace("_", " ")} · ${Math.round((e.starting_balance || 0) / 1000)}k
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs font-mono">
            <div className="font-bold" style={{ color: pnlPositive ? "#10b981" : "#ef4444" }}>
              {money(e.current_pnl_pct)}
            </div>
            <div className="text-[#9ca3af]">DD <span className="text-[#f59e0b]">{Number(e.current_drawdown_pct || 0).toFixed(2)}%</span></div>
            <div className="text-[#9ca3af]">
              {passed
                ? `Passed in ${e.days_since_start}d`
                : `Day ${e.days_since_start}${e.days_remaining ? ` · ${e.days_remaining} left` : ""}`}
            </div>
          </div>
          <div className="mt-1.5">
            <span
              className="inline-block px-2 py-0.5 rounded font-mono text-[9px] uppercase tracking-widest border"
              style={{ color: statusColor, borderColor: `${statusColor}55`, background: `${statusColor}18` }}
            >
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Leaderboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = !!user;
  const [tab, setTab] = useState("active");
  const [firm, setFirm] = useState("");
  const [sort, setSort] = useState("best_pnl");
  const [entries, setEntries] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", tab === "hall" ? "passed" : "active");
      params.set("sort", sort);
      params.set("limit", "50");
      if (firm) params.set("firm", firm);
      const r = await publicApi.get(`/leaderboard?${params.toString()}`);
      setEntries(r.data?.entries || []);
    } catch {
      setEntries([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await publicApi.get("/leaderboard/firms");
        setFirms(r.data?.firms || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => { load(); }, [tab, firm, sort]);

  const shown = useMemo(() => entries, [entries]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]" data-testid="leaderboard-page">
      <header className="border-b border-[#1f2937] bg-[#0a0e1a] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="leaderboard-back-btn"
                className="flex items-center gap-1 text-xs text-[#9ca3af] hover:text-white"
              >
                <ArrowLeft className="w-3 h-3" /> Back to dashboard
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-[#3b82f6]/15 border border-[#3b82f6]/40 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-[#3b82f6]" />
                </div>
                <div className="font-heading font-black tracking-tighter text-lg">VEEROX</div>
              </div>
            )}
          </div>
          {!isLoggedIn && (
            <Link
              to="/register"
              data-testid="leaderboard-cta-signup"
              className="h-9 px-4 rounded bg-[#f59e0b] hover:bg-[#d97706] text-black font-semibold text-xs inline-flex items-center gap-2"
            >
              <Trophy className="w-3.5 h-3.5" /> Sign up to compete
            </Link>
          )}
          {isLoggedIn && user && (
            <div className="text-xs text-[#9ca3af] font-mono">Signed in as {user.email}</div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="vx-label">Public leaderboard</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Challenge Leaderboard
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl">
            Live progress rings for prop-firm challenges powered by Veerox SMC signals.
            Publish your own from the Prop Challenge page — anonymised by design.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#0a0e1a] border border-[#1f2937]">
              <TabsTrigger value="active" data-testid="tab-active" className="text-xs font-mono">
                Active Challenges
              </TabsTrigger>
              <TabsTrigger value="hall" data-testid="tab-hall-of-fame" className="text-xs font-mono">
                🏆 Hall of Fame
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-[10px] font-mono text-[#6b7280] uppercase tracking-widest">
              <Filter className="w-3 h-3" /> firm
            </div>
            <Select value={firm || "__all"} onValueChange={(v) => setFirm(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-[160px] h-8 bg-[#111827] border-[#1f2937] text-xs font-mono" data-testid="firm-filter">
                <SelectValue placeholder="All firms" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
                <SelectItem value="__all">All firms</SelectItem>
                {firms.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[170px] h-8 bg-[#111827] border-[#1f2937] text-xs font-mono" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-[#9ca3af] font-mono text-sm py-10 text-center">Loading leaderboard…</div>
        ) : shown.length === 0 ? (
          <div className="vx-card p-10 text-center" data-testid="leaderboard-empty">
            <Trophy className="w-10 h-10 text-[#6b7280] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading text-xl font-bold">No entries yet</div>
            <p className="text-sm text-[#9ca3af] mt-2 max-w-md mx-auto">
              Be the first to publish your challenge. Go to your Prop Challenge page and click Publish.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="leaderboard-grid">
            {shown.map((e) => <EntryCard key={e.id} e={e} />)}
          </div>
        )}

        <div className="pt-6 text-center">
          <a
            href="https://veerox.ai"
            target="_blank" rel="noopener noreferrer"
            className="text-[10px] font-mono text-[#6b7280] hover:text-[#9ca3af] inline-flex items-center gap-1"
          >
            Powered by Veerox AI Trading <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </main>
    </div>
  );
}
